import { Component, ErrorInfo, ReactNode } from 'react';

import { Alert } from '@/components/ui/alerts/Alert.comp.tsx';
import { DataOperationsError } from '@/errorHandling/DataOperationsError.util.tsx';

import { DataOperationsErrorContext } from './DataOperationsErrorContext';
import { FallbackUI } from './fallbackUI/FallbackUI.comp.tsx';

type Props = { children: ReactNode; onError?: (error: unknown, errorInfo: ErrorInfo) => void };
type State = {
  context: {
    handleDataOperationError: (error: DataOperationsError) => void;
    handleMinorError: (message: string) => void;
    handleOperationSuccess: (message?: string) => void;
  };
  fallbackUIErrorDetails: ReactNode | null;
  fallbackUIErrorMessage: string | null;
  isCorrupted: boolean;
  isMinorError: boolean;
  isSuccess: boolean;
  minorErrorMessage: string | null;
  successMessage: string | null;
};

const IGNORED_ERRORS = ['undefined has no properties', 'is undefined'];

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      context: {
        handleDataOperationError: this.handleDataOperationError,
        handleMinorError: this.handleMinorError,
        handleOperationSuccess: this.handleOperationSuccess,
      },
      fallbackUIErrorDetails: null,
      fallbackUIErrorMessage: null,
      isCorrupted: false,
      isMinorError: false,
      isSuccess: false,
      minorErrorMessage: null,
      successMessage: null,
    };
  }

  componentDidCatch(error: DataOperationsError, errorInfo: ErrorInfo) {
    this.props.onError?.(error, errorInfo);
    this.setState({
      fallbackUIErrorDetails: errorInfo.componentStack,
      fallbackUIErrorMessage: error.message,
      isCorrupted: true,
    });
  }

  handleDataOperationError = (error: DataOperationsError) => {
    this.setState({
      fallbackUIErrorDetails: toDataOperationsFallbackUIMessage(error),
      fallbackUIErrorMessage: error.message,
      isCorrupted: true,
    });
  };

  handleMinorError = (message: string) => {
    if (IGNORED_ERRORS.some(error => message.toLowerCase().includes(error))) {
      return;
    }
    this.setState({
      isMinorError: true,
      minorErrorMessage: message,
    });
  };

  handleOperationSuccess = (message?: string) => {
    this.setState({
      isSuccess: true,
      successMessage: message ?? null,
    });
  };

  render() {
    const {
      fallbackUIErrorDetails,
      fallbackUIErrorMessage,
      isCorrupted,
      isMinorError,
      isSuccess,
      minorErrorMessage,
      successMessage,
    } = this.state;
    if (isCorrupted) {
      return (
        <FallbackUI errorDetails={fallbackUIErrorDetails} errorMessage={fallbackUIErrorMessage} />
      );
    }
    return (
      <DataOperationsErrorContext value={this.state.context}>
        {this.props.children}
        {isMinorError && (
          <Alert
            handleAlertShown={this.resetMinorErrorState}
            message={minorErrorMessage}
            type="error"
          />
        )}
        {isSuccess && (
          <Alert
            handleAlertShown={this.resetSuccessState}
            message={successMessage}
            type="success"
          />
        )}
      </DataOperationsErrorContext>
    );
  }

  resetMinorErrorState = () => {
    this.setState({
      isMinorError: false,
      minorErrorMessage: null,
    });
  };

  resetSuccessState = () => {
    this.setState({
      isSuccess: false,
      successMessage: null,
    });
  };
}

const toDataOperationsFallbackUIMessage = (error: DataOperationsError): ReactNode => {
  const { code, method, timestamp, url } = error;
  return (
    <>
      {!!timestamp && <>Timestamp: {timestamp}</>}
      <br />
      {!!code && <>Code: {code}</>}
      <br />
      {!!url && <>Url: {url}</>}
      <br />
      {!!method && <>Method: {method}</>}
    </>
  );
};
